import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import { doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';

/**
 * Hook to manage completely isolated week data
 * Each week gets its own dedicated database path for true isolation
 * 
 * @param {string} workspaceId - Workspace ID
 * @param {string} weekId - Week ID (YYYY-MM-DD format of Sunday)
 * @returns {Object} Week data and update functions
 */
export const useWeekData = (workspaceId, weekId) => {
  console.log('useWeekData hook called:', { workspaceId, weekId });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Week-specific data (completely isolated)
  const [weekDates, setWeekDates] = useState([]);
  const [dailyRevenue, setDailyRevenue] = useState({});
  const [weeklyPayroll, setWeeklyPayroll] = useState(0);
  const [startingBalance, setStartingBalance] = useState(0);
  const [purchases, setPurchases] = useState({});
  const [customProfitTax, setCustomProfitTax] = useState({});

  // Initialize a new week in Firebase (automatically creates and saves)
  const initializeWeekInFirebase = useCallback(async (sundayDate) => {
    if (!workspaceId || !sundayDate) return;

    try {
      console.log('Initializing new week in Firebase:', sundayDate);
      
      // Generate week dates (7 days starting from Sunday)
      const dates = [];
      const start = new Date(sundayDate);
      for (let i = 0; i < 7; i++) {
        const date = new Date(start);
        date.setDate(start.getDate() + i);
        dates.push(date);
      }

      // Initialize default data
      const defaultWeekDates = dates;
      const defaultDailyRevenue = {};
      const defaultWeeklyPayroll = 0;
      const defaultStartingBalance = 0;
      const defaultPurchases = {};
      const defaultCustomProfitTax = {};

      // Save all data to Firebase atomically
      const batch = [
              // Save metadata with timezone-safe date strings
      setDoc(doc(db, `workspaces/${workspaceId}/weeks/${sundayDate}_metadata`), {
        weekDates: defaultWeekDates.map(date => {
          // Save as YYYY-MM-DD format to avoid timezone issues
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}T00:00:00.000Z`;
        }),
        weeklyPayroll: defaultWeeklyPayroll,
        startingBalance: defaultStartingBalance,
        lastUpdated: new Date(),
        createdAt: new Date()
      }),

        // Save daily revenue
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${sundayDate}_revenue`), {
          dailyRevenue: defaultDailyRevenue,
          lastUpdated: new Date(),
          createdAt: new Date()
        }),

        // Save purchases
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${sundayDate}_purchases`), {
          purchases: defaultPurchases,
          lastUpdated: new Date(),
          createdAt: new Date()
        }),

        // Save custom settings
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${sundayDate}_settings`), {
          customProfitTax: defaultCustomProfitTax,
          lastUpdated: new Date(),
          createdAt: new Date()
        })
      ];

      // Execute all saves
      await Promise.all(batch);

      // Update local state
      console.log('🆕 Setting initialized weekDates:', {
        sundayDate,
        generatedDates: defaultWeekDates,
        firstDate: defaultWeekDates[0]?.toDateString(),
        lastDate: defaultWeekDates[6]?.toDateString()
      });
      setWeekDates(defaultWeekDates);
      setDailyRevenue(defaultDailyRevenue);
      setWeeklyPayroll(defaultWeeklyPayroll);
      setStartingBalance(defaultStartingBalance);
      setPurchases(defaultPurchases);
      setCustomProfitTax(defaultCustomProfitTax);

      console.log('New week initialized and saved to Firebase:', sundayDate);
    } catch (error) {
      console.error('Error initializing week in Firebase:', error);
      setError(error.message);
      throw error;
    }
  }, [workspaceId]); // Removed getWeekPath dependency - now using inline paths

  // Load week data from Firestore
  const loadWeekData = useCallback(async () => {
    if (!workspaceId || !weekId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('Loading week data for:', weekId);

      // Load week metadata (dates, payroll, balance)
      const metadataDoc = await getDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_metadata`));
      if (metadataDoc.exists()) {
        const data = metadataDoc.data();
        // Parse dates safely without timezone conversion
        const loadedWeekDates = data.weekDates?.map(dateStr => {
          // Extract just the date part (YYYY-MM-DD) and create local date
          const datePart = dateStr.split('T')[0];
          const [year, month, day] = datePart.split('-').map(Number);
          return new Date(year, month - 1, day); // Create local date without timezone issues
        }) || [];
        console.log('📅 Setting weekDates from Firebase:', {
          weekId,
          rawDates: data.weekDates,
          parsedDates: loadedWeekDates,
          firstDate: loadedWeekDates[0]?.toDateString(),
          lastDate: loadedWeekDates[6]?.toDateString()
        });
        setWeekDates(loadedWeekDates);
        setWeeklyPayroll(data.weeklyPayroll || 0);
        setStartingBalance(data.startingBalance || 0);
        
        // Load daily revenue
        const revenueDoc = await getDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_revenue`));
        if (revenueDoc.exists()) {
          setDailyRevenue(revenueDoc.data().dailyRevenue || {});
        } else {
          setDailyRevenue({});
        }

        // Load purchases
        const purchasesDoc = await getDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_purchases`));
        if (purchasesDoc.exists()) {
          setPurchases(purchasesDoc.data().purchases || {});
        } else {
          setPurchases({});
        }

        // Load custom profit/tax settings
        const settingsDoc = await getDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_settings`));
        if (settingsDoc.exists()) {
          setCustomProfitTax(settingsDoc.data().customProfitTax || {});
        } else {
          setCustomProfitTax({});
        }

        console.log('Week data loaded successfully:', weekId);
      } else {
        // Week doesn't exist in Firebase - initialize it automatically
        console.log('🆕 Week not found in Firebase, initializing new week:', weekId);
        await initializeWeekInFirebase(weekId);
      }
    } catch (error) {
      console.error('Error loading week data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, weekId]); // Removed all function dependencies to break circular dependency

  // Save all week data (atomic operation)
  const saveWeekData = useCallback(async () => {
    if (!workspaceId || !weekId || isSaving) return;
    
    setIsSaving(true);
    try {
      console.log('Saving week data for:', weekId);

      // Create batch operations to save all week data atomically
      const batch = [
        // Save metadata with timezone-safe date strings
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_metadata`), {
          weekDates: weekDates.map(date => {
            // Save as YYYY-MM-DD format to avoid timezone issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}T00:00:00.000Z`;
          }),
          weeklyPayroll,
          startingBalance,
          lastUpdated: new Date()
        }),

        // Save daily revenue
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_revenue`), {
          dailyRevenue,
          lastUpdated: new Date()
        }),

        // Save purchases
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_purchases`), {
          purchases,
          lastUpdated: new Date()
        }),

        // Save custom settings
        setDoc(doc(db, `workspaces/${workspaceId}/weeks/${weekId}_settings`), {
          customProfitTax,
          lastUpdated: new Date()
        })
      ];

      // Execute all saves
      await Promise.all(batch);
      
      setLastSaved(new Date());
      console.log('Week data saved successfully:', weekId);
    } catch (error) {
      console.error('Error saving week data:', error);
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  }, [workspaceId, weekId, weekDates, dailyRevenue, weeklyPayroll, startingBalance, purchases, customProfitTax, isSaving]); // Removed getWeekPath dependency

  // Initialize week data if it doesn't exist
  const initializeWeek = useCallback((sundayDate) => {
    const dates = [];
    const start = new Date(sundayDate);
    
    // Generate 7 days starting from Sunday
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    
    setWeekDates(dates);
    setDailyRevenue({});
    setWeeklyPayroll(0);
    setStartingBalance(0);
    setPurchases({});
    setCustomProfitTax({});
    
    console.log('Initialized new week for date:', sundayDate);
  }, []); // Removed weekId dependency to prevent re-renders

  // Load data when weekId changes
  useEffect(() => {
    console.log('useWeekData useEffect triggered - weekId:', weekId);
    if (weekId) {
      console.log('Loading week data for weekId:', weekId);
      loadWeekData();
    } else {
      console.log('No weekId provided, skipping loadWeekData');
    }
  }, [weekId, loadWeekData]); // Now properly includes loadWeekData - no more infinite loop because getWeekPath is memoized

  // Individual update functions
  const updateDailyRevenue = useCallback((dateStr, amount) => {
    setDailyRevenue(prev => ({
      ...prev,
      [dateStr]: parseFloat(amount) || 0
    }));
  }, []);

  const updateWeeklyPayroll = useCallback((amount) => {
    setWeeklyPayroll(parseFloat(amount) || 0);
  }, []);

  const updateStartingBalance = useCallback((amount) => {
    setStartingBalance(parseFloat(amount) || 0);
  }, []);

  const addPurchase = useCallback((dateStr, purchase) => {
    setPurchases(prev => ({
      ...prev,
      [dateStr]: [...(prev[dateStr] || []), purchase]
    }));
  }, []);

  const removePurchase = useCallback((dateStr, purchaseId) => {
    setPurchases(prev => ({
      ...prev,
      [dateStr]: (prev[dateStr] || []).filter(p => p.id !== purchaseId)
    }));
  }, []);

  const updateCustomProfitTaxForDate = useCallback((dateStr, settings) => {
    setCustomProfitTax(prev => ({
      ...prev,
      [dateStr]: settings
    }));
  }, []);

  return {
    // State
    loading,
    error,
    isSaving,
    lastSaved,
    
    // Week data
    weekDates,
    dailyRevenue,
    weeklyPayroll,
    startingBalance,
    purchases,
    customProfitTax,
    
    // Actions
    initializeWeek,
    saveWeekData,
    loadWeekData,
    
    // Update functions
    updateDailyRevenue,
    updateWeeklyPayroll,
    updateStartingBalance,
    addPurchase,
    removePurchase,
    updateCustomProfitTaxForDate,
    
    // Direct setters for complex updates
    setWeekDates,
    setDailyRevenue,
    setPurchases,
    setCustomProfitTax
  };
};